import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ahjApi } from "@/services/ahjService";
import { IAHJ, AHJType, AHJFormData } from "@/types/ahj";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { toast } from "sonner";
import { US_STATES } from "@/data/states";
import { Loader2 } from "lucide-react";

const initialFormData: AHJFormData = {
  name: "",
  type: AHJType.CITY,
  state: "",
  permittingRequirements: {
    slabUpliftTest: false,
    slabCoreTest: false,
    adaPlans: false,
    photometricPlans: false,
    egressPlans: false,
    smokeVentInspections: false,
    fpeStamp: false,
    weightSigns: false,
    flueKeepersPalletStops: false,
    keepClearsStickers: false,
    fireSignsCommoditySigns: false,
  },
  requiredSubmittalProcess: "",
  avgReviewTimeline: "",
  expeditedServices: "",
  expeditedServicesCost: "",
};

const permittingRequirementsLabels: Record<string, string> = {
  slabUpliftTest: "Slab Uplift Test",
  slabCoreTest: "Slab Core Test",
  adaPlans: "ADA Plans",
  photometricPlans: "Photometric Plans",
  egressPlans: "Egress Plans",
  smokeVentInspections: "Smoke Vent Inspections",
  fpeStamp: "FPE Stamp",
  weightSigns: "Weight Signs",
  flueKeepersPalletStops: "Flue Keepers Pallet Stops",
  keepClearsStickers: "Keep Clears Stickers",
  fireSignsCommoditySigns: "Fire Signs/Commodity Signs",
};

interface AHJFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAHJ?: IAHJ | null;
  onSuccess?: (ahj: IAHJ) => void;
}

export function AHJFormDialog({
  open,
  onOpenChange,
  editingAHJ,
  onSuccess,
}: AHJFormDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<AHJFormData>(initialFormData);

  // Reset form when dialog opens/closes or editingAHJ changes
  useEffect(() => {
    if (open) {
      if (editingAHJ) {
        setFormData({
          name: editingAHJ.name,
          type: editingAHJ.type,
          state: editingAHJ.state,
          permittingRequirements: editingAHJ.permittingRequirements,
          requiredSubmittalProcess: editingAHJ.requiredSubmittalProcess || "",
          avgReviewTimeline: editingAHJ.avgReviewTimeline || "",
          expeditedServices: editingAHJ.expeditedServices || "",
          expeditedServicesCost: editingAHJ.expeditedServicesCost || "",
        });
      } else {
        setFormData(initialFormData);
      }
    }
  }, [open, editingAHJ]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: ahjApi.createAHJ,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ahjs"] });
      toast.success("AHJ created successfully");
      onOpenChange(false);
      setFormData(initialFormData);
      if (onSuccess) onSuccess(data.ahj);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || "Failed to create AHJ");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AHJFormData> }) =>
      ahjApi.updateAHJ(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ahjs"] });
      toast.success("AHJ updated successfully");
      onOpenChange(false);
      setFormData(initialFormData);
      if (onSuccess) onSuccess(data.ahj);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || "Failed to update AHJ");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.state) {
      toast.error("Name and state are required");
      return;
    }

    if (editingAHJ) {
      updateMutation.mutate({ id: editingAHJ._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setFormData(initialFormData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-border/50 shadow-sophisticated">
        <DialogHeader className="border-b border-border/50 pb-4">
          <DialogTitle className="text-xl font-semibold">{editingAHJ ? "Edit AHJ" : "Add New AHJ"}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {editingAHJ
              ? "Update the AHJ information below"
              : "Enter the details for the new AHJ"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-foreground/80">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter city/county name"
                required
                className="bg-background/50 focus:bg-background transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type" className="text-sm font-medium text-foreground/80">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData({ ...formData, type: value as AHJType })
                }
              >
                <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AHJType.CITY}>City</SelectItem>
                  <SelectItem value={AHJType.COUNTY}>County</SelectItem>
                  <SelectItem value={AHJType.SPECIAL}>Special</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state" className="text-sm font-medium text-foreground/80">State *</Label>
              <Select
                value={formData.state}
                onValueChange={(value) =>
                  setFormData({ ...formData, state: value })
                }
              >
                <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground/80">Permitting Requirements</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-border/50 rounded-lg p-4 bg-muted/10">
              {Object.entries(permittingRequirementsLabels).map(
                ([key, label]) => (
                  <div key={key} className="flex items-center justify-between space-x-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                    <Label
                      htmlFor={key}
                      className="text-sm font-normal cursor-pointer flex-1 text-foreground/90"
                    >
                      {label}
                    </Label>
                    <Switch
                      id={key}
                      checked={
                        formData.permittingRequirements[
                          key as keyof typeof formData.permittingRequirements
                        ]
                      }
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          permittingRequirements: {
                            ...formData.permittingRequirements,
                            [key]: checked,
                          },
                        })
                      }
                    />
                  </div>
                )
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="submittalProcess" className="text-sm font-medium text-foreground/80">Required Submittal Process</Label>
            <div className="border border-border/50 rounded-md overflow-hidden">
              <RichTextEditor
                content={formData.requiredSubmittalProcess}
                onChange={(text, html) =>
                  setFormData({
                    ...formData,
                    requiredSubmittalProcess: html,
                  })
                }
                placeholder="Describe the submittal process..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="reviewTimeline" className="text-sm font-medium text-foreground/80">Average Review Timeline</Label>
              <Input
                id="reviewTimeline"
                value={formData.avgReviewTimeline}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    avgReviewTimeline: e.target.value,
                  })
                }
                placeholder="e.g., 2-3 weeks"
                className="bg-background/50 focus:bg-background transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expeditedServices" className="text-sm font-medium text-foreground/80">Expedited Services</Label>
              <Input
                id="expeditedServices"
                value={formData.expeditedServices}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expeditedServices: e.target.value,
                  })
                }
                placeholder="Yes/No or description"
                className="bg-background/50 focus:bg-background transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expeditedCost" className="text-sm font-medium text-foreground/80">Expedited Services Cost</Label>
              <Input
                id="expeditedCost"
                value={formData.expeditedServicesCost}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expeditedServicesCost: e.target.value,
                  })
                }
                placeholder="e.g., $500"
                className="bg-background/50 focus:bg-background transition-colors"
              />
            </div>
          </div>

          <DialogFooter className="border-t border-border/50 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel} className="hover:bg-accent hover:text-accent-foreground transition-colors">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-gradient shadow-md hover:shadow-lg transition-all"
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingAHJ ? (
                "Update AHJ"
              ) : (
                "Create AHJ"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
